import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Container from '@mui/material/Container'
import Button from '@mui/material/Button'
import MuiLink from '@mui/material/Link'
import Table from '@mui/material/Table'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Dialog from '@mui/material/Dialog'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardActionArea from '@mui/material/CardActionArea'
import Badge from '@mui/material/Badge'
import AddIcon from '@mui/icons-material/Add'

import { useContextHook } from '../store/context'
import { toDateString } from '../utils/dateUtils'
import ListHeader from '../components/listheader/listheader'
import { get, isOsxSafari, setPageTitle } from '../utils/service'
import { PagedResponse } from '../types/pagedResponse'
import Alert from '../components/alert/alert'
import { getEvaluators } from '../utils/cuppingUtils'
import ResponsiveTableHeader from '../components/fields/responsiveTableHeader'
import { CuppingSession } from '../types/cuppingSession'
import { CuppingSessionStatus } from '../types/enums/cuppingSessionStatus'
import Pagination from '../components/fields/pagination'
import { userLoaded } from '../store/actions'
import SortableTableHead from '../components/fields/sortableTableHead'
import { SortType } from '../types/enums/sortType'

const getLoadCuppingSessionsUrl = (customerId: number, searchString: string): string => {
  const searchFragment = searchString ? `&search=${searchString}` : ''
  return `cuppingsessions/?customer=${customerId}${searchFragment}`
}

const CuppingView = (): JSX.Element => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { state, dispatch } = useContextHook()
  const navigate = useNavigate()

  const [cuppingSessionsList, setCuppingSessionsList] = React.useState<Array<CuppingSession> | null>(null)
  const [loadCuppingsUrl, setLoadCuppingsUrl] = React.useState<string>('')
  const [paginationSettings, setPaginationSettings] = React.useState<PagedResponse<CuppingSession> | null>(null)
  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const [hasLoadError, setHasLoadError] = React.useState<boolean>(false)
  const [officeCuppingUrl, setOfficeCuppingUrl] = React.useState<string>('')
  const [sortFragment, setSortFragment] = React.useState<string>('')
  const [searchString, setSearchString] = React.useState<string>('')

  const isScreenBelowMdBreakpoint = useMediaQuery(theme.breakpoints.down('md'))

  React.useEffect(() => {
    if (loadCuppingsUrl) {
      setCuppingSessionsList(null)
      setIsLoading(true)
      setHasLoadError(false)
      get<PagedResponse<CuppingSession>>(loadCuppingsUrl)
        .then((response) => {
          setIsLoading(false)
          setPaginationSettings(response.data)
          setCuppingSessionsList(response.data.results)
        })
        .catch(() => {
          setIsLoading(false)
          setHasLoadError(true)
        })
    }
  }, [loadCuppingsUrl])

  React.useEffect(() => {
    setPageTitle(t('cupping.cuppingsessions'))
  }, [t])

  React.useEffect(() => {
    if (state.currentCustomer?.id) {
      setPaginationSettings(null)
      const sortFragmentString = sortFragment ? `&ordering=${sortFragment}` : ''
      setLoadCuppingsUrl(`${getLoadCuppingSessionsUrl(state.currentCustomer.id, searchString)}${sortFragmentString}`)
    }
  }, [state.currentCustomer?.id, searchString, sortFragment])

  const onSort = (newSortFragment: string): void => {
    setSortFragment(newSortFragment)
  }

  const renderRow = (cuppingSession: CuppingSession): JSX.Element => {
    return (
      <TableRow key={cuppingSession.id} className="colored-row">
        <TableCell>
          <ResponsiveTableHeader header={t('cupping.sessionname')} />
          {isOsxSafari() ? (
            <MuiLink component={Link} to={`/cuppings/${cuppingSession.id}/`}>
              {cuppingSession.name}
            </MuiLink>
          ) : (
            <>
              <Button className="link-cell" component={Link} to={`/cuppings/${cuppingSession.id}/`} />
              {cuppingSession.name}
            </>
          )}
        </TableCell>
        <TableCell>
          <ResponsiveTableHeader header={t('cupping.date')} />
          {toDateString(cuppingSession.created)}
        </TableCell>
        <TableCell>
          <ResponsiveTableHeader header={t('cupping.cuppingform')} />
          {cuppingSession.cupping_form_data.name}
        </TableCell>
        <TableCell>
          <ResponsiveTableHeader header={t('cupping.samples')} />
          {cuppingSession.cupping_samples.length}
        </TableCell>
        <TableCell>
          <ResponsiveTableHeader header={t('cupping.evaluators')} />
          {getEvaluators(cuppingSession).join(', ')}
        </TableCell>
      </TableRow>
    )
  }

  return (
    <Container component="main" maxWidth={false}>
      <ListHeader
        headerText={t('cupping.cuppingsessions')}
        searchPlaceholder={t('cupping.searchcuppingsessions')}
        typeString={t('cupping.cuppinglistheader')}
        totalResults={paginationSettings?.count || 0}
        onSearch={(newSearchString: string): void => {
          if (searchString !== newSearchString) {
            setSearchString(newSearchString)
          }
        }}
      >
        <Tooltip title={t('cupping.newcuppingsession')}>
          <Badge sx={{ mr: 1 }} badgeContent={(state.cuppingRoastIds || []).length} color="warning">
            <IconButton component={Link} color="primary" to={`/cuppings/create`}>
              <AddIcon />
            </IconButton>
          </Badge>
        </Tooltip>
      </ListHeader>
      {!!officeCuppingUrl && !!localStorage.getItem('roest-office-cupping-names') && (
        <Dialog open={true} fullScreen>
          <Stack direction="row" flexWrap="wrap">
            {JSON.parse(localStorage.getItem('roest-office-cupping-names') || '[]').map((x: string) => {
              return (
                <Button
                  key={x}
                  sx={{ height: 130, width: 130, m: 1 }}
                  variant="outlined"
                  onClick={() => {
                    if (state.user) {
                      dispatch(
                        userLoaded({
                          ...state.user,
                          first_name: x,
                        })
                      )
                    }
                    localStorage.removeItem('roest-completed-cupping-sessions')
                    localStorage.removeItem('roest-cupping-session-temp-scores')
                    navigate(officeCuppingUrl)
                  }}
                >
                  {x}
                </Button>
              )
            })}
          </Stack>
        </Dialog>
      )}
      {cuppingSessionsList && window.location.search.indexOf('roestcup') > -1 && (
        <Dialog open={true} fullScreen>
          <Stack direction="row" flexWrap="wrap">
            {cuppingSessionsList
              .filter((x) => x.status === CuppingSessionStatus.InProgress)
              .map((x) => {
                return (
                  <Card key={x.id} sx={{ m: 1, width: '31%', display: 'flex' }}>
                    <CardActionArea onClick={() => setOfficeCuppingUrl(`/cuppings/addscore/${x.id}/`)}>
                      <CardContent>
                        <Typography variant="h3">
                          {x.cupping_samples
                            .filter((x) => !!x.log_data)
                            .map((x) => `#${x.log_data?.batch_no}`)
                            .join(', ') || x.cupping_samples[0].external_log_name}
                        </Typography>
                        <Typography variant="caption">
                          {`Evaluators: ${x.session_scores.map((x) => x.evaluator_name).join(', ')}`}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                )
              })}
          </Stack>
        </Dialog>
      )}
      {cuppingSessionsList && (
        <>
          {isScreenBelowMdBreakpoint && (
            <Badge badgeContent={(state.cuppingRoastIds || []).length} color="warning">
              <Button component={Link} variant="contained" to={`/cuppings/create`}>
                {t('cupping.newcuppingsession')}
              </Button>
            </Badge>
          )}
          <TableContainer>
            <Table className="colored-table collapse-table">
              <TableHead>
                <TableRow>
                  <SortableTableHead
                    sortFragment={sortFragment}
                    sortProp="name"
                    sortType={SortType.String}
                    onSort={onSort}
                  >
                    {cuppingSessionsList.length > 0 &&
                    cuppingSessionsList.filter((x) => x.status === CuppingSessionStatus.InProgress).length === 0
                      ? t('cupping.completedsessions')
                      : t('cupping.sessionname')}
                  </SortableTableHead>
                  <SortableTableHead
                    sortFragment={sortFragment}
                    sortProp="created"
                    sortType={SortType.Date}
                    onSort={onSort}
                  >
                    {t('cupping.date')}
                  </SortableTableHead>
                  <SortableTableHead
                    sortFragment={sortFragment}
                    sortProp="cupping_form_name"
                    sortType={SortType.String}
                    onSort={onSort}
                  >
                    {t('cupping.cuppingform')}
                  </SortableTableHead>
                  <SortableTableHead
                    sortFragment={sortFragment}
                    sortProp="samples_count"
                    sortType={SortType.Number}
                    onSort={onSort}
                  >
                    {t('cupping.samples')}
                  </SortableTableHead>
                  <SortableTableHead
                    sortFragment={sortFragment}
                    sortProp="evaluators_count"
                    sortType={SortType.Number}
                    onSort={onSort}
                  >
                    {t('cupping.evaluators')}
                  </SortableTableHead>
                </TableRow>
              </TableHead>
              <TableBody>
                {cuppingSessionsList
                  .filter((x) => x.status === CuppingSessionStatus.InProgress)
                  .map((x) => renderRow(x))}
                {cuppingSessionsList.filter((x) => x.status === CuppingSessionStatus.Done).length > 0 &&
                  cuppingSessionsList.filter((x) => x.status === CuppingSessionStatus.InProgress).length > 0 && (
                    <TableRow>
                      <TableCell colSpan={10}>{t('cupping.completedsessions')}</TableCell>
                    </TableRow>
                  )}
                {cuppingSessionsList.filter((x) => x.status === CuppingSessionStatus.Done).map((x) => renderRow(x))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      {isLoading && <CircularProgress />}
      <Stack sx={{ mt: 1, mb: 4 }}>
        {paginationSettings && (
          <Pagination
            isLoading={isLoading}
            paginationSettings={paginationSettings}
            onChangePage={(newPageUrl: string) => setLoadCuppingsUrl(newPageUrl)}
          />
        )}
      </Stack>
      {hasLoadError && <Alert severity="error">{t('cupping.loadsessionserror')}</Alert>}
      {!isLoading && !hasLoadError && cuppingSessionsList && cuppingSessionsList.length === 0 && (
        <Alert severity="info">{t('cupping.nosessions')}</Alert>
      )}
    </Container>
  )
}

export default CuppingView
